import {useState, useMemo, useEffect} from 'react';
import useApi from '../../../utils/useApi';
import {useForm} from 'react-hook-form';
import {states} from "../../../utils/states";
import {formatters, validationRules} from '../../../utils/formFormatAndValidate';
import {Eye, EyeOff} from "lucide-react";
import Button from "../../UI/Button";
import {useNavigate} from "react-router-dom";
import {IoEye, IoEyeOff} from "react-icons/io5";

const STATES = states;

const FILING_STATUSES = [
    { value: 'Single', label: 'Single' },
    { value: 'Head of Household', label: 'Head of Household' },
    { value: 'Married Filing Joint Return', label: 'Married Filing Joint Return' },
    { value: 'Married Filing Seperately', label: 'Married Filing Separately' },
    { value: 'Qualifying Widow', label: 'Qualifying Widow' }
];

const HEAR_ABOUT_US = [
    { value: 'Facebook', label: 'Facebook' },
    { value: 'Walk On', label: 'Walk-in' },
    { value: 'Flyer', label: 'Flyer' },
    { value: 'referral_hear', label: 'Referral - If so name' },
    { value: 'Internet Search', label: 'Internet Search' },
    { value: 'Yelp', label: 'Yelp' },
    { value: 'Thumbtack', label: 'Thumbtack' }
];

const DOCUMENT_COPIES = [
    { value: 'Office Pick Up', label: 'Office Pick Up' },
    { value: 'Portal Upload', label: 'Portal Upload' }
];

const PortalRequestForm = ({ handleShowHeader, handleShowSettingsHeader }) => {
    const { isLoading, savePortalRequest, getPortalPreparers } = useApi();
    const [preparers, setPreparers] = useState([]);
    const [showPassword, setShowPassword] = useState({});
    const [message, setMessage] = useState('');
    const [dependents, setDependents] = useState([]);
    const [successMessage, setSuccessMessage] = useState(null);
    const navigate = useNavigate();

    const {
        register,
        handleSubmit,
        watch,
        formState: { errors },
        reset,
        setValue,
    } = useForm({
                    defaultValues:{
                        account_type: '',
                        client: '',
                        client_id: '',
                        password: '',
                        email: '',
                        phone_primary: '',
                        phone_secondary: '',
                        ssn_id: '',
                        occupation: '',
                        address: '',
                        client_dob: '',
                        filing_status: '',
                        state_return: '',
                        st_return: '',
                        rt_number: '',
                        act_number: '',
                        bank_name: '',
                        hear_aboutus: '',
                        document_copies: '',
                        dependents: 0,
                        dep_name: [],
                        dep_rel: [],
                        dep_dob: [],
                        dep_ssn: [],
                        new_client: '',
                        is_client_old_new: '',
                        point_of_contact: '',
                        preferred_preparer: '',
                        spouse_name: '',
                        spouse_dob: '',
                        spouse_ssn: '',
                        spouse_occupation: '',
                        spouse_dod: '',
                        is_returning_client: ''
                    },
                        /*{
                            "account_type": "personal",
                            "client": "Brenden Leib",
                            "client_id": "brendenleib999",
                            "password": "brenden",
                            "email": "brendenleib@gmail.com",
                            "phone_primary": "360-306-2409",
                            "phone_secondary": "444-444-4444",
                            "ssn_id": "444-44-4444",
                            "occupation": "Engineer",
                            "address": "5874 Milwaukee Rd.",
                            "client_dob": "",
                            "filing_status": "",
                            "state_return": "yes_state_return",
                            "st_return": "WA",
                            "rt_number": "444444444",
                            "act_number": "444444444444",
                            "bank_name": "Bank of America",
                            "hear_aboutus": "",
                            "document_copies": "Office Pick Up",
                            "dependents": "have_dependents",
                            "dep_name": [
                                "Emmeline Leib"
                            ],
                            "dep_rel": [
                                "Daughter"
                            ],
                            "dep_dob": [
                                "2022-06-02"
                            ],
                            "dep_ssn": [
                                "333-33-3333"
                            ],
                            "is_client_old_new": 1
                        }*/
                });

    const accountType = watch('account_type');
    const fillingStatus = watch('filing_status');
    const stateReturn = watch('state_return');
    const newClient = watch('new_client');
    const hearAboutUs = watch('hear_aboutus');
    const hasDependents = watch('dependents');

    useEffect(() => {
        handleShowSettingsHeader(false);
        handleShowHeader(true);
        initialize();
    }, []);

    const initialize = async() => {
        const response = await getPortalPreparers();

        setPreparers(response);
    }

    const handleFormattedChange = (e, formatter) => {
        const { name, value } = e.target;
        const formattedValue = formatters[formatter](value);
        setValue(name, formattedValue, { shouldValidate: true });
    };

    const formFields = useMemo(() => ({
        basicInfo: {
            title: 'Basic Information',
            fields: [
                {
                    name: 'account_type',
                    label: 'Account Type',
                    type: 'select',
                    required: true,
                    options: [
                        { value: 'personal', label: 'Personal' },
                        { value: 'business', label: 'Business' }
                    ],
                    validation: { required: 'Account type is required' }
                },
                {
                    name: 'client',
                    label: accountType === 'business' ? 'Legal Entity Name' : 'Full Name',
                    type: 'text',
                    required: true,
                    placeholder: accountType === 'business' ? 'Enter legal business name' : 'Enter full name',
                    validation: { required: 'Name is required' }
                },
                {
                    name: 'client_id',
                    label: 'Username',
                    type: 'text',
                    required: true,
                    placeholder: 'Choose a username (min 6 characters)',
                    validation: {
                        required: validationRules.required.message,
                        minLength: { value: 6, message: 'Username must be at least 6 characters' }
                    }
                },
                {
                    name: 'password',
                    label: 'Password',
                    type: 'password',
                    required: true,
                    placeholder: 'Choose a password (min 6 characters)',
                    validation: {
                        required: 'Password is required',
                        minLength: { value: 6, message: 'Password must be at least 6 characters' }
                    }
                }
            ]
        },
        contactInfo: {
            title: 'Contact Information',
            fields: [
                {
                    name: 'email',
                    label: 'Email',
                    type: 'email',
                    required: true,
                    placeholder: 'Enter your email address',
                    validation: {
                        required: validationRules.required.message,
                        pattern: validationRules.email
                    }
                },
                {
                    name: 'phone_primary',
                    label: 'Primary Phone',
                    type: 'tel',
                    required: true,
                    formatter: 'phone',
                    placeholder: 'XXX-XXX-XXXX',
                    validation: {
                        required: validationRules.required.message,
                        pattern: validationRules.phone
                    }
                },
                {
                    name: 'phone_secondary',
                    label: 'Secondary Phone',
                    type: 'tel',
                    formatter: 'phone',
                    placeholder: 'XXX-XXX-XXXX',
                    validation: {
                        pattern: validationRules.phone
                    }
                },
                {
                    name: 'address',
                    label: 'Mailing Address',
                    type: 'textarea',
                    required: true,
                    placeholder: 'Enter your mailing address',
                    validation: { required: 'Address is required' }
                }
            ]
        },

        personalInfo: {
            title: 'Personal Information',
            fields: [
                {
                    name: 'client_dob',
                    label: 'Date of Birth',
                    type: 'date',
                    required: accountType === 'personal',
                    validation: { required: 'Date of birth is required' },
                    show: accountType === 'personal'
                },
                {
                    name: 'contact_primary',
                    label: 'Primary Contact (Name)',
                    type: 'text',
                    required: accountType === 'business',
                    placeholder: 'Enter primary contact name',
                    validation: { required: 'Primary contact is required' },
                    show: accountType === 'business'
                },
                {
                    name: 'ssn_id',
                    label: accountType === 'business' ? 'Federal Tax ID' : 'SSN',
                    type: 'password',
                    required: true,
                    formatter: accountType === 'business' ? 'ein':'ssn',
                    placeholder: accountType === 'business' ? 'XX-XXXXXXX' : 'XXX-XX-XXXX',
                    validation: {
                        required: validationRules.required.message,
                        pattern: accountType === 'business' ? validationRules.ein:validationRules.ssn
                    },
                    showPasswordToggle: true

                },
                {
                    name: 'occupation',
                    label: accountType === 'business' ? 'Type of Business' : 'Occupation',
                    type: accountType === 'business' ? 'select' : 'text',
                    required: true,
                    options: accountType === 'business' ? [
                        { value: 'Corporation', label: 'Corporation' },
                        { value: 'S-Corp', label: 'S-Corp' },
                        { value: 'Partnership', label: 'Partnership' },
                        { value: 'Limited Liability Corporation', label: 'Limited Liability Company (Multi Member Only)' },
                        { value: 'Trustee/estate', label: 'Trustee/estate' },
                        { value: 'Nonprofit Organization', label: 'Nonprofit Organization' },
                        { value: 'Other', label: 'Other' }
                    ] : [],
                    validation: { required: 'This field is required' }
                }
            ]
        },
        filingStatus: {
            title: 'Filing Status',
            show: accountType === 'personal',
            fields: [
                {
                    name: 'filing_status',
                    label: 'Filing Status',
                    type: 'select',
                    required: true,
                    options: FILING_STATUSES,
                    validation: { required: 'Filing status is required' }
                }
            ]
        },
        spouseInfo: {
            title: 'Spouse Information',
            show: fillingStatus === 'Married Filing Joint Return' || fillingStatus === 'Married Filing Seperately',
            fields: [
                {
                    name: 'spouse_name',
                    label: 'Spouse\'s Name',
                    type: 'text',
                    required: true,
                    placeholder: 'Enter spouse\'s name as it appears on their SS Card',
                    validation: { required: 'Spouse\'s name is required' }
                },
                {
                    name: 'spouse_dob',
                    label: 'Spouse\'s Date of Birth',
                    type: 'date',
                    required: true,
                    validation: { required: 'Spouse\'s date of birth is required' }
                },
                {
                    name: 'spouse_email',
                    label: "Spouse's Email",
                    type: 'email',
                    required: true,
                    placeholder: "Enter your spouse's email address",
                    validation: {
                        required: validationRules.required.message,
                        pattern: validationRules.email
                    }
                },
                {
                    name: 'spouse_ssn',
                    label: 'Spouse\'s SSN',
                    type: 'password',
                    required: true,
                    formatter: 'ssn',
                    placeholder: 'XXX-XX-XXXX',
                    validation: {
                        required: 'Spouse\'s SSN is required',
                        pattern: validationRules.ssn
                    },
                    showPasswordToggle: true
                },
                {
                    name: 'spouse_occupation',
                    label: 'Spouse\'s Occupation',
                    type: 'text',
                    required: true,
                    placeholder: 'Enter spouse\'s occupation',
                    validation: { required: 'Spouse\'s occupation is required' }
                }
            ]
        },
        widowInfo: {
            title: 'Widow Information',
            show: fillingStatus === 'Qualifying Widow',
            fields: [
                {
                    name: 'spouse_dod',
                    label: 'Spouse\'s Date of Death',
                    type: 'date',
                    required: true,
                    validation: { required: 'Spouse\'s date of death is required' }
                }
            ]
        },
        /*clientType: {
            title: 'Client Type',
            fields: [
                {
                    name: 'is_returning_client',
                    label: 'Are you a new client or a returning client?',
                    type: 'select',
                    required: true,
                    options: [
                        { value: '0', label: 'New Client' },
                        { value: '1', label: 'Returning Client' }
                    ],
                    validation: { required: 'Please select your client status' }
                }
            ]
        },*/
    }), [accountType, fillingStatus]);

    const renderField = ({ name, label, type, required, placeholder, options, validation, formatter }) => {
        const baseClasses = "w-full p-3 border rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent";

        if (formatter) {
            switch (type) {
                case 'password':
                    return (
                        <div className="relative">
                            <input
                                type={showPassword[name] ? 'text' : 'password'}
                                {...register(name, validation)}
                                onChange={formatter ? (e) => handleFormattedChange(e, formatter) : undefined}
                                className={baseClasses}
                                placeholder={placeholder}
                                required={required}
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(prev => ({ ...prev, [name]: !prev[name] }))}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400"
                            >
                                {showPassword[name] ? <EyeOff/> : <Eye/>}
                            </button>
                        </div>
                    );
                default:
                    return (
                        <input
                            type={type}
                            {...register(name, validation)}
                            onChange={(e) => handleFormattedChange(e, formatter)}
                            className={baseClasses}
                            placeholder={placeholder}
                            required={required}

                        />
                    );
            }
        }

        switch (type) {
            case 'select':
                return (
                    <select
                        {...register(name, validation)}
                        className={baseClasses}
                        required={required}

                    >
                        <option value="">Select {label}</option>
                        {options.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                );

            case 'textarea':
                return (
                    <textarea
                        {...register(name, validation)}
                        className={`${baseClasses} min-h-[100px]`}
                        placeholder={placeholder}
                        required={required}

                    />
                );

            case 'password':
                return (
                    <div className="relative">
                        <input
                            type={showPassword[name] ? 'text' : 'password'}
                            {...register(name, validation)}
                            onChange={formatter ? (e) => handleFormattedChange(e, formatter) : undefined}
                            className={baseClasses}
                            placeholder={placeholder}
                            required={required}
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword(prev => ({ ...prev, [name]: !prev[name] }))}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400"
                        >
                            {showPassword[name] ? <EyeOff className="h-5 w-5"/> : <Eye className="h-5 w-5"/>}
                        </button>
                    </div>
                );

            default:
                return (
                    <input
                        type={type}
                        {...register(name, validation)}
                        className={baseClasses}
                        placeholder={placeholder}
                        required={required}


                    />
                );
        }
    };

    const addDependent = () => {
        if (dependents.length < 10) {
            setDependents(prev => [...prev, { id: prev.length }]);
        }
    };

    const removeDependent = (index) => {
        setDependents(prev => prev.filter((_, i) => i !== index));
        setValue(`dep_name[${index}]`, '');
        setValue(`dep_rel[${index}]`, '');
        setValue(`dep_dob[${index}]`, '');
        setValue(`dep_ssn[${index}]`, '');
    };

    const handleBack = () => {
        navigate('/');
    }

    const onSubmit = async (data) => {
        console.log(data);
        try {
            const response = await savePortalRequest(data);

            console.log(response);
            if (response.status === 'success') {
                setMessage('Thank You! Your client portal request has been received. Instructions for access will be emailed once approved.');
                reset();
                setDependents([]);

                setSuccessMessage(response.message);
            }
        }
        catch (error) {
            console.error('Form submission error:', error);
        }
    };

    if (successMessage) {
        return (
            <div className="container mx-auto px-4 py-8  z-10 relative text-center">
                { successMessage }

                <Button className="flex row mx-auto my-8" onClick={handleBack}>Back To Site</Button>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-8  z-10 relative">
            <div className="max-w-4xl mx-auto bg-white dark:bg-gray-800 rounded-lg shadow-lg">
                <div className="p-8">
                    <h2 className="text-3xl font-light text-center text-gray-800 dark:text-gray-200 mb-8">
                        Portal Request
                    </h2>

                    {message && (
                        <div className="mb-6 p-4 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-100 rounded-lg text-center">
                            {message}
                        </div>
                    )}

                    <div className="mb-8 text-gray-600 dark:text-gray-300 text-center">
                        <p className="mb-4">
                            When you register for a portal, we will be send an email notifying us of your
                            pending registration for approval. Once we have approved your registration, you
                            will receive a confirmation email.
                        </p>
                        <p>
                            We appreciate the opportunity to be of service and look forward to working with you.
                        </p>
                    </div>

                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
                        {Object.entries(formFields).map(([section, { title, fields, show }]) => (
                            show !== false && (
                                <div key={title} className="space-y-6">
                                    <h3 className="text-xl font-medium text-gray-700 dark:text-gray-300">
                                        {title}
                                    </h3>
                                    <div className="grid grid-cols-1 gap-6">
                                        {fields.map(field => {
                                            if (field.show === false) return null;

                                            return (
                                                <div key={field.name} className="space-y-2">
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                        {field.label}
                                                        {field.required && <span className="text-red-500 ml-1">*</span>}
                                                    </label>
                                                    {renderField(field)}
                                                    {errors[field.name] && (
                                                        <p className="text-red-500 text-sm">{errors[field.name].message}</p>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )
                        ))}

                        {/* Dependent Fields Section */}
                        {accountType === 'personal' && (
                            <div className="space-y-6">
                                <h3 className="text-xl font-medium text-gray-700 dark:text-gray-300">
                                    Dependents
                                </h3>
                                <div className="space-y-4">
                                    <select
                                        {...register('dependents')}
                                        className="w-full p-3 border rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 border-gray-300 dark:border-gray-600"
                                    >
                                        <option value="0">No Dependents</option>
                                        <option value="1">Yes, I have dependents</option>
                                    </select>

                                    {hasDependents == 1 && (
                                        <div className="space-y-6">
                                            {dependents.map((dep, index) => (
                                                <div key={dep.id} className="p-4 border rounded-lg dark:border-gray-600 space-y-4">
                                                    <h4 className="font-medium text-gray-700 dark:text-gray-300">
                                                        Dependent {index + 1}
                                                    </h4>
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                        <div>
                                                            <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">
                                                                Name
                                                            </label>
                                                            <input
                                                                {...register(`dep_name[${index}]`, { required: 'Name is required' })}
                                                                className="w-full p-2 border rounded dark:bg-gray-700"
                                                                required={true}
                                                                placeholder="Dependent's name"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">
                                                                Relationship
                                                            </label>
                                                            <input
                                                                {...register(`dep_rel[${index}]`, { required: 'Relationship is required' })}
                                                                className="w-full p-2 border rounded dark:bg-gray-700"
                                                                required={true}
                                                                placeholder="Relationship to dependent"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">
                                                                Date of Birth
                                                            </label>
                                                            <input
                                                                type="date"
                                                                {...register(`dep_dob[${index}]`, { required: 'Date of birth is required' })}
                                                                required={true}
                                                                className="w-full p-2 border rounded dark:bg-gray-700"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">
                                                                SSN
                                                            </label>
                                                            <div className="relative">
                                                                <input
                                                                    type={showPassword[`dep_ssn_${index}`] ? 'text' : 'password'}
                                                                    {...register(`dep_ssn[${index}]`, {
                                                                        required: validationRules.required.message,
                                                                        pattern: validationRules.ssn
                                                                    })}
                                                                    onChange={(e) => handleFormattedChange(e, 'ssn')}
                                                                    className="w-full p-2 border rounded dark:bg-gray-700"
                                                                    required={true}
                                                                    placeholder="XXX-XX-XXXX"
                                                                />
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setShowPassword(prev => ({
                                                                        ...prev,
                                                                        [`dep_ssn_${index}`]: !prev[`dep_ssn_${index}`]
                                                                    }))}
                                                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400"
                                                                >
                                                                    {showPassword[`dep_ssn_${index}`] ? <EyeOff className="h-5 w-5"/> : <Eye className="h-5 w-5"/>}
                                                                </button>
                                                            </div>
                                                        </div>
                                                        <div className="md:col-span-2">
                                                            <button
                                                                type="button"
                                                                onClick={() => removeDependent(index)}
                                                                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                                                            >
                                                                Remove Dependent
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}

                                            {dependents.length < 10 && (
                                                <button
                                                    type="button"
                                                    onClick={addDependent}
                                                    className="w-full p-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                                                >
                                                    Add Dependent
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* State Return Section */}
                        {accountType === 'personal' && (
                            <div className="space-y-6">
                                <h3 className="text-xl font-medium text-gray-700 dark:text-gray-300">
                                    State Return Information
                                </h3>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Will you be filing a state return?
                                        </label>
                                        <select
                                            {...register('state_return')}
                                            className="w-full p-3 border rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 border-gray-300 dark:border-gray-600"
                                        >
                                            <option value="">Please Select</option>
                                            <option value="yes_state_return">Yes</option>
                                            <option value="No">No</option>
                                        </select>
                                    </div>

                                    {stateReturn === 'yes_state_return' && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Select State
                                            </label>
                                            <select
                                                {...register('st_return')}
                                                className="w-full p-3 border rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 border-gray-300 dark:border-gray-600"
                                            >
                                                <option value="">Select State</option>
                                                {STATES.map(state => (
                                                    <option key={state.value} value={state.value}>
                                                        {state.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Banking Information */}
                        <div className="space-y-6">
                            <h3 className="text-xl font-medium text-gray-700 dark:text-gray-300">
                                Banking Information
                            </h3>
                            <p className="text-gray-600 dark:text-gray-400">
                                If you are due a refund and would like a direct deposit, please provide the following:
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Bank Routing Number
                                        <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showPassword.routing ? 'text' : 'password'}
                                            {...register('rt_number', {
                                                pattern: {
                                                    value: /^\d{9}$/,
                                                    message: 'Routing number must be 9 digits'
                                                }
                                            })}
                                            onChange={(e) => handleFormattedChange(e, 'numeric')}
                                            maxLength={9}
                                            className="w-full p-3 border rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 border-gray-300 dark:border-gray-600"
                                            placeholder="Enter routing number"
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(prev => ({ ...prev, routing: !prev.routing }))}
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400"
                                        >
                                            {showPassword.routing ? <EyeOff className="h-5 w-5"/> : <Eye className="h-5 w-5"/>}
                                        </button>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Account Number
                                        <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showPassword.account ? 'text' : 'password'}
                                            {...register('act_number')}
                                            className="w-full p-3 border rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 border-gray-300 dark:border-gray-600"
                                            placeholder="Enter account number"
                                            required

                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(prev => ({ ...prev, account: !prev.account }))}
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400"
                                        >
                                            {showPassword.account ? <EyeOff className="h-5 w-5"/> : <Eye className="h-5 w-5"/>}
                                        </button>
                                    </div>
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Bank Name
                                        <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        {...register('bank_name')}
                                        className="w-full p-3 border rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 border-gray-300 dark:border-gray-600"
                                        placeholder="Enter bank name"
                                        required
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Document Delivery Preference */}
                        <div className="space-y-6">
                            <h3 className="text-xl font-medium text-gray-700 dark:text-gray-300">
                                Document Delivery Preference
                            </h3>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    How would you like to receive your document copies?
                                </label>
                                <select
                                    {...register('document_copies', { required: 'Please select a delivery method' })}
                                    className="w-full p-3 border rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 border-gray-300 dark:border-gray-600"
                                >
                                    <option value="">Please Select</option>
                                    {DOCUMENT_COPIES.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        {/* New Client Section */}
                        <div className="space-y-6">
                            <h3 className="text-xl font-medium text-gray-700 dark:text-gray-300">
                                Additional Information
                            </h3>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Preferred Preparer? <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        {...register('preferred_preparer', { required: 'Please select' })}
                                        className="w-full p-3 border rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 border-gray-300 dark:border-gray-600"
                                    >
                                        <option value="">Please Select</option>
                                        {preparers.map((preparer) => (
                                            <option key={preparer.id} value={preparer.id}>
                                                {preparer.profile.client}
                                            </option>
                                        ))}

                                    </select>
                                    {errors.new_client && (
                                        <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                                            {errors.new_client.message}
                                        </p>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Are You A New Client This Year? <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        {...register('new_client', { required: 'Please select an option' })}
                                        className="w-full p-3 border rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 border-gray-300 dark:border-gray-600"
                                    >
                                        <option value="">Please Select</option>
                                        <option value="1">Yes</option>
                                        <option value="0">No</option>
                                    </select>
                                    {errors.new_client && (
                                        <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                                            {errors.new_client.message}
                                        </p>
                                    )}
                                </div>

                                {newClient == '1' && (
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                How did you hear about us? <span className="text-red-500">*</span>
                                            </label>
                                            <select
                                                {...register('hear_aboutus', {
                                                    required: newClient === '1' ? 'Please select how you heard about us' : false
                                                })}
                                                className="w-full p-3 border rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 border-gray-300 dark:border-gray-600"
                                            >
                                                <option value="">Please Select</option>
                                                {HEAR_ABOUT_US.map(option => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                            {errors.hear_aboutus && (
                                                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                                                    {errors.hear_aboutus.message}
                                                </p>
                                            )}
                                        </div>

                                        {hearAboutUs === 'referral_hear' && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                    Referral Name <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    {...register('referral', {
                                                        required: hearAboutUs === 'referral_hear' ? 'Please enter referral name' : false
                                                    })}
                                                    className="w-full p-3 border rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 border-gray-300 dark:border-gray-600"
                                                    placeholder="Enter the name of who referred you"
                                                />
                                                {errors.referral && (
                                                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                                                        {errors.referral.message}
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Submit Buttons */}
                        <div className="flex justify-end space-x-4 pt-6">
                            <button
                                type="button"
                                onClick={() => {
                                    reset();
                                    setDependents([]);
                                }}
                                className="px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                            >
                                Reset Form
                            </button>
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                            >
                                {isLoading ? 'Submitting...' : 'Submit Request'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default PortalRequestForm;
